import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { MarkdownComponent } from 'ngx-markdown';
import { Observable, tap } from 'rxjs';
import { BasePage } from 'src/app/@common/abstract/page.abstract';
import { EventService } from 'src/app/@domain/event/application/event.service';
import { ImageDirective } from '../../../@common/DOM/image.directive';
import { ToDatePipe } from '../../../@common/pipe/to-date.pipe';
import { Event } from '../../../@generated';
import { CardGridComponent } from '../../../@ui/app/content/card-grid/card-grid.component';
import { RelatedCategoriesComponent } from '../../../@ui/app/content/related-categories/related-categories.component';
import { VideoItemComponent } from '../../../@ui/app/content/sections/video-item/video-item.component';
import { EditButtonComponent } from '../../../@ui/app/global/edit-button/edit-button.component';
import { SectionHeaderComponent } from '../../../@ui/app/global/section-header/section-header.component';
import { SortComponent } from '../../../@ui/app/global/sort/sort.component';
import { SpinnerComponent } from '../../../@ui/app/global/spinner/spinner.component';

@Component({
    selector: 'app-event-page',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        VideoItemComponent,
        SpinnerComponent,
        AsyncPipe,
        CardGridComponent,
        SortComponent,
        SectionHeaderComponent,
        ImageDirective,
        MarkdownComponent,
        RelatedCategoriesComponent,
        ToDatePipe,
        EditButtonComponent
    ],
    styles: `
        @use "variables" as *;

        :host {
            width: 100%;
            display: flex;
            flex-direction: column;
            flex: 1;
        }

        .event-header-banner {
            display: flex;
            align-items: flex-end;
            position: relative;
            color: $white;
            overflow: hidden;
            width: 100%;
            max-height: 320px;
            aspect-ratio: 21 / 9;

            @media (max-width: 996px) {
                aspect-ratio: 21/7;
            }

            .background-image {
                position: absolute;
                width: 100%;
                max-width: $width;
                height: 100%;
                object-fit: cover;
                object-position: center;
                background-repeat: no-repeat !important;
                background-size: cover !important;
                background-position: center !important;
                z-index: 0;
            }

            .header-container {
                position: relative;
                height: 100%;
                margin: auto;
                max-width: $width;
                width: 100%;
            }

            .container {
                height: 100%;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                padding: 0.25rem 1rem 0;
                margin: 0 !important;

                h1, p {
                    z-index: 5;
                }

                p {
                    font-size: 1.1rem;
                    margin-bottom: 1rem;

                    @media (max-width: 1000px) {
                        font-size: 1rem;
                    }
                }
            }

            &:after {
                content: "";
                position: absolute;
                width: 100%;
                height: 100%;
                background: rgba(0, 0, 0, .3);
            }

            h1 {
                overflow: hidden;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                margin-bottom: 0.4rem;
                font-size: 3.2rem;

                @media (max-width: 1000px) {
                    font-size: 3rem;
                }

                @media (max-width: 900px) {
                    font-size: 2.5rem;
                }

                @media screen and (max-width: 600px) {
                    font-size: 2.2rem;
                }

                @media screen and (max-width: 500px) {
                    font-size: 1.9rem;
                }
            }
        }

        .description {
            margin: 1rem auto 0;

            .subtitle {
                display: flex;
                font-weight: 600;
                padding-bottom: 0.5rem;
                font-size: 1.4rem;

                @media screen and (max-width: 600px) {
                    font-size: 1.25rem;
                }

                h4 {
                    font-weight: 600;
                    font-size: 1.4rem;
                    width: fit-content;
                }
            }

            markdown {
                line-height: 1.75rem;
                color: $grey-dark;
            }

            .clip {
                overflow: hidden;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                line-clamp: 3;
                -webkit-box-orient: vertical;
            }

            .read-more {
                color: $red;
                text-decoration: underline;
                display: block;
                cursor: pointer;
            }
        }

        app-related-categories {
            margin-top: 2rem;
        }

        ::ng-deep markdown p {
            line-height: 1.35rem;
        }

        .container {
            margin: 1.5rem auto;

            @media screen and (min-width: 768px) {
                margin: 0.5rem auto;
            }
        }

        .chapter-container {
            display: flex;
            flex-direction: column;
        }

        .btn-load-more {
            display: block;
            margin: 2rem auto 0;
        }

        app-filters {
            margin-bottom: 1rem;
        }

        app-card-grid {
            margin-bottom: 2rem;
        }

        .sections-title {
            display: flex;
            font-size: 1.5rem;
            padding-top: 1rem;

            @media screen and (max-width: 768px) {
                padding-top: 0;
            }

            h2 {
                color: $black;
                font-size: 1.5rem;
                width: fit-content;
            }
        }

    `,
    template: `
        @if (event$ | async; as event) {
            <div class="event-header-banner" [title]="event.title">
                <div class="header-container">
                    <img [src]="event.banner" alt="{{ event.title }}" loading="eager" class="background-image">
                    <div class="container">
                        <h1>{{ event.title }}</h1>

                        <p>{{ event.total_videos }} Videos · {{ event.date | toDate }}</p>
                    </div>
                </div>
            </div>

            <div class="description container">
                <section>
                    <div class="subtitle">
                        <h4>About</h4>
                        @if (isAdmin()) {
                            <app-edit-button [url]="'/admin/events/' + event.id"/>
                        }
                    </div>

                    @if (event.description; as description) {
                        <markdown [data]="description" [class.clip]="readMore()"></markdown>
                        @if (description.length > 50) {
                            <button class="read-more" (click)="readMore.set(!readMore())">
                                {{ readMore() ? "Read More" : "Read Less" }}
                            </button>
                        }
                    }

                    @if (!event.description) {
                        <p class="no-content">Description is not available</p>
                    }
                </section>

                @if (event.categories.length) {
                    <app-related-categories [categories]="event.categories"/>
                }
            </div>
            <section class="container">
                <div class="sections-title">
                    <h2>Videos</h2>
                    @if (isAdmin()) {
                        <app-edit-button [url]="'/admin/events/' + event.id" [queryParams]="{tab: 'sections'}"/>
                    }
                </div>

                @if (event.sections.length || event.unsectioned_videos.length) {
                    <app-filters view/>
                }

                <section class="chapter-container">
                    @for (section of event.sections; track section.title) {
                        @if (section.title) {
                            <app-section-header [headline]="section.title" isSubHeadline/>
                        }
                        
                        <div class="section-description" style="margin-bottom: 0.75rem !important">
                          <div [innerHTML]="section.description"></div>
                        </div>

                        <app-card-grid [listView]="!isGridView()">
                            @for (video of section.videos; track video.id) {
                                <app-video-item [video]="video" [part]="video.parts[0]" [listView]="!isGridView()"/>
                            }
                        </app-card-grid>
                    }

                    @if (event.unsectioned_videos.length) {
                        <app-card-grid [listView]="!isGridView()">
                            @for (video of event.unsectioned_videos; track video.id) {
                                <app-video-item [video]="video" [part]="video.parts[0]" [listView]="!isGridView()"/>
                            }
                        </app-card-grid>
                    }
                </section>

                @if (!event.sections.length && !event.unsectioned_videos.length) {
                    <p class="no-content mt-2">Event does not have any videos</p>
                }
            </section>
        } @else {
            <app-spinner/>
        }
    `
})
export default class EventPage extends BasePage implements OnInit {
    private eventService = inject(EventService);

    @Input({ required: true }) slug!: string;
    public event$?: Observable<Event>;
    public readMore = signal(false);

    public ngOnInit(): void {
        this.event$ = this.eventService.getEventBySlug(this.slug).pipe(
            tap(event => this.setMeta({
                title: event.title,
                description: event.description,
                image: event.thumbnail,
                index: true
            })),
            tap(({ description }) => {
                if (description?.length ?? 0 > 200) {
                    this.readMore.set(true);
                }
            })
        );
    }
}
